import React, { useState, useRef, useEffect } from "react";
import Highlighter from "react-highlight-words";
import ClickOut from "react-click-out";
import _ from "lodash";
import classnames from "classnames/bind";
import style from "./index.module.scss";
import {
  IMake,
  ISearchState as IState,
  IVehicleCategory,
  Replacement,
} from "../../../interface";
import localesReplacement from "../../../utils/localesReplacement";

type DataListType = string[] | IMake[] | IState[] | IVehicleCategory[] | null;

interface IFilterDropdownProps {
  dataList: DataListType;
  filter: string;
  changeHandler: (value: string) => void;
  type: string;
  replacements: Replacement;
  placeholder?: string;
  disabled?: boolean;
}

const cx = classnames.bind(style);

function FilterDropdown(props: IFilterDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  const {
    dataList,
    filter,
    changeHandler,
    type,
    replacements,
    placeholder,
    disabled,
  } = props;
  const [visible, setVisible] = useState(false);
  const [textFilter, setTextFilter] = useState<string>("");

  useEffect(() => {
    if (!filter) {
      setTextFilter("");
    }
  }, [dataList?.length]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedKeyRegex = /[ a-zA-Z0-9,.-]/;
    if (event.target.value) {
      if (allowedKeyRegex.test(_.last(event.target.value) ?? "") === false) {
        return;
      }
      if (!_.isEqual(filter, textFilter)) {
        setTextFilter(event.target.value);
      } else {
        if (event.target.value.length > filter.length) {
          setTextFilter(
            _.replace(
              event.target.value,
              new RegExp(_.escapeRegExp(filter)),
              ""
            )
          );
        } else {
          setTextFilter(filter);
        }
      }
    } else {
      setTextFilter(filter);
    }
  };

  const filterFn = (item: any) => {
    if (_.isEqual(textFilter, filter)) {
      return item;
    }
    const searchTokens = textFilter.split(" ");
    if (typeof item === "string") {
      if (searchTokens.length === 1) {
        return (
          item.toLowerCase().search(_.escapeRegExp(textFilter.toLowerCase())) >=
          0
        );
      } else {
        return searchTokens.every(
          (token: string) =>
            item.toLowerCase().search(_.escapeRegExp(token.toLowerCase())) >= 0
        );
      }
    } else if (item && "name" in item) {
      if (searchTokens.length === 1) {
        return (
          item.name
            .toLowerCase()
            .search(_.escapeRegExp(textFilter.toLowerCase())) >= 0
        );
      } else {
        return searchTokens.every(
          (token: string) =>
            item.name
              .toLowerCase()
              .search(_.escapeRegExp(token.toLowerCase())) >= 0
        );
      }
    } else {
      return item;
    }
  };

  const toggle = () => {
    if (!disabled && !_.isEmpty(dataList)) {
      setVisible(!visible);
    }
  };

  const setClickOutState = () => {
    setVisible(false);
  };

  const applyHandler = (value: string) => {
    if (!disabled) {
      setTextFilter(value);
      changeHandler(value);
      setVisible(false);
    }
  };

  const clear = (evt: React.MouseEvent) => {
    if (!disabled) {
      setTextFilter("");
      if (filter) {
        changeHandler("");
        setVisible(false);
      } else {
        (ref.current as any).focus();
      }
      evt.stopPropagation();
    }
  };

  return (
    <ClickOut className="click-out" onClickOut={setClickOutState}>
      <div
        ref={ref}
        tabIndex={0}
        className={cx("filter-dropdown", {
          open: visible,
          disabled: _.isEmpty(dataList) || disabled,
        })}
      >
        <div className={cx("block")} onClick={toggle}>
          <div className={cx("meta")}>
            <span className={cx("label")}>{type}</span>
            <input
              className={cx("note")}
              placeholder={localesReplacement(
                replacements,
                placeholder ? placeholder : "-- Search or choose --"
              )}
              onChange={handleChange}
              value={textFilter}
              disabled={_.isEmpty(dataList) || disabled}
            />
          </div>
        </div>
        {visible && !disabled && (
          <div className={cx("dropdown")}>
            <ul className={cx("options")}>
              {_.chain(dataList)
                .filter(filterFn)
                .map(
                  (
                    data: IMake | IState | string | IVehicleCategory,
                    index: number
                  ) => (
                    <li
                      className={cx({
                        actived: _.isEqual(
                          filter,
                          _.isString(data) ? data : data?.name
                        ),
                      })}
                      onClick={() =>
                        applyHandler(_.isString(data) ? data : data["name"])
                      }
                      key={index}
                      title={_.isString(data) ? data : _.get(data, "name", "")}
                    >
                      <Highlighter
                        highlightClassName={cx("highlight")}
                        searchWords={textFilter.split(" ")}
                        autoEscape={true}
                        textToHighlight={
                          _.isString(data) ? data : _.get(data, "name", "")
                        }
                      />
                    </li>
                  )
                )
                .value()}
            </ul>

            {!_.isEmpty(filter || textFilter) && (
              <button className={cx("apply-btn")} onClick={clear}>
                {localesReplacement(replacements, "Remove filters")}
              </button>
            )}
          </div>
        )}
      </div>
    </ClickOut>
  );
}

export default FilterDropdown;
